import React, { useState, useEffect, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { makeStyles, Typography, IconButton, Tooltip } from '@material-ui/core'
import { PhonelinkRing, ChevronLeft, ChevronRight, Devices, Lock } from '@material-ui/icons'
import { useIntl } from 'react-intl'
import { CapeeshColors } from '../../../assets/ColorPalette'
import { studentScreenMessages } from '../StudentScreen.i18n.Messages'
import StudentAccessCodeDialog from './StudentAccessCodeDialog'
import StudentSetPasswordDialog from './StudentSetPasswordDialog'

const SIDEBAR_WIDTH_EXPANDED = 220
const SIDEBAR_WIDTH_COLLAPSED = 60
const COLLAPSE_BREAKPOINT = 1000

const useStyles = makeStyles((theme) => ({
    sidebar: {
        minHeight: 'calc(100vh - 60px)',
        backgroundColor: '#fff',
        borderRight: `1px solid ${CapeeshColors.Gray4}`,
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 0',
        transition: 'width 200ms ease, min-width 200ms ease',
        overflow: 'hidden',
    },
    sidebarDrawer: {
        minHeight: 0,
        borderRight: 'none',
    },
    toggleButton: {
        alignSelf: 'flex-end',
        margin: '0 8px 8px',
    },
    navItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 20px',
        textDecoration: 'none',
        color: CapeeshColors.Gray900,
        borderRadius: '0 8px 8px 0',
        marginRight: 12,
        transition: 'background-color 150ms ease',
        whiteSpace: 'nowrap',
        '&:hover': {
            backgroundColor: CapeeshColors.Gray100,
        },
    },
    navItemCollapsed: {
        justifyContent: 'center',
        padding: '10px 0',
        marginRight: 0,
        borderRadius: 0,
    },
    navItemActive: {
        backgroundColor: CapeeshColors.Primary100,
        color: CapeeshColors.Primary600,
        fontWeight: 600,
        '&:hover': {
            backgroundColor: CapeeshColors.Primary100,
        },
    },
    navIcon: {
        fontSize: 20,
        flexShrink: 0,
    },
    navLabel: {
        fontSize: 14,
        fontWeight: 'inherit',
        lineHeight: 1.4,
    },
    sectionLabel: {
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: CapeeshColors.Gray1,
        padding: '20px 20px 8px',
        whiteSpace: 'nowrap',
    },
}))

const StudentSidebar = ({ inDrawer = false }) => {
    const classes = useStyles()
    const location = useLocation()
    const intl = useIntl()

    const [showAccessCodeDialog, setShowAccessCodeDialog] = useState(false)
    const [showPasswordDialog, setShowPasswordDialog] = useState(false)

    const forceExpanded = inDrawer
    const [expanded, setExpanded] = useState(() => inDrawer || window.innerWidth >= COLLAPSE_BREAKPOINT)

    useEffect(() => {
        if (inDrawer) return
        const handleResize = () => {
            if (window.innerWidth < COLLAPSE_BREAKPOINT) {
                setExpanded(false)
            }
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [inDrawer])

    const toggleExpanded = useCallback(() => setExpanded((prev) => !prev), [])

    const isActive = (path) => location.pathname === path

    const isExpanded = forceExpanded || expanded
    const sidebarWidth = isExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED

    return (
        <nav
            className={`${classes.sidebar} ${inDrawer ? classes.sidebarDrawer : ''}`}
            style={inDrawer ? undefined : { width: sidebarWidth, minWidth: sidebarWidth }}
        >
            {!inDrawer && (
                <IconButton
                    className={classes.toggleButton}
                    size="small"
                    onClick={toggleExpanded}
                    aria-label={isExpanded ? intl.formatMessage(studentScreenMessages.collapseSidebar) : intl.formatMessage(studentScreenMessages.expandSidebar)}
                >
                    {isExpanded ? <ChevronLeft /> : <ChevronRight />}
                </IconButton>
            )}

            {isExpanded && (
                <Typography className={classes.sectionLabel}>{intl.formatMessage(studentScreenMessages.navigation)}</Typography>
            )}

            <Tooltip title={isExpanded ? '' : intl.formatMessage(studentScreenMessages.useAppInstead)} placement="right">
                <Link
                    to="/student/unity"
                    className={`${classes.navItem} ${
                        !isExpanded ? classes.navItemCollapsed : ''
                    } ${isActive('/student/unity') ? classes.navItemActive : ''}`}
                >
                    <PhonelinkRing className={classes.navIcon} />
                    {isExpanded && (
                        <Typography className={classes.navLabel}>{intl.formatMessage(studentScreenMessages.useAppInstead)}</Typography>
                    )}
                </Link>
            </Tooltip>

            {isExpanded && (
                <Typography className={classes.sectionLabel}>{intl.formatMessage(studentScreenMessages.account)}</Typography>
            )}

            <Tooltip title={isExpanded ? '' : intl.formatMessage(studentScreenMessages.loginOnAnotherDevice)} placement="right">
                <div
                    onClick={() => setShowAccessCodeDialog(true)}
                    className={`${classes.navItem} ${!isExpanded ? classes.navItemCollapsed : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    <Devices className={classes.navIcon} />
                    {isExpanded && (
                        <Typography className={classes.navLabel}>{intl.formatMessage(studentScreenMessages.loginOnAnotherDevice)}</Typography>
                    )}
                </div>
            </Tooltip>

            <Tooltip title={isExpanded ? '' : intl.formatMessage(studentScreenMessages.setPassword)} placement="right">
                <div
                    onClick={() => setShowPasswordDialog(true)}
                    className={`${classes.navItem} ${!isExpanded ? classes.navItemCollapsed : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    <Lock className={classes.navIcon} />
                    {isExpanded && (
                        <Typography className={classes.navLabel}>{intl.formatMessage(studentScreenMessages.setPassword)}</Typography>
                    )}
                </div>
            </Tooltip>

            <StudentAccessCodeDialog
                open={showAccessCodeDialog}
                onClose={() => setShowAccessCodeDialog(false)}
            />
            <StudentSetPasswordDialog
                open={showPasswordDialog}
                onClose={() => setShowPasswordDialog(false)}
            />
        </nav>
    )
}

export { SIDEBAR_WIDTH_EXPANDED, SIDEBAR_WIDTH_COLLAPSED }
export default StudentSidebar
